"use client";

import { c as _c } from "react/compiler-runtime";
import { InitialConfigType, LexicalComposer } from "@lexical/react/LexicalComposer";
import { forwardRef, useImperativeHandle, useCallback, useEffect, useRef } from "react";
import { EditorState, RootNode, COMMAND_PRIORITY_CRITICAL, createCommand } from "lexical";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { ListNode, ListItemNode } from "@lexical/list";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { MentionedUser, ReferencedNft } from "@/entities/IDrop";
import { ActiveDropAction } from "@/types/dropInteractionTypes";
import { MentionNode } from "../drops/create/lexical/nodes/MentionNode";
import { HashtagNode } from "../drops/create/lexical/nodes/HashtagNode";
import { ImageNode } from "../drops/create/lexical/nodes/ImageNode";
import ExampleTheme from "../drops/create/lexical/ExampleTheme";
import { assertUnreachable } from "@/helpers/AllowlistToolHelpers";
import ClearEditorPlugin, { ClearEditorPluginHandles } from "../drops/create/lexical/plugins/ClearEditorPlugin";
import NewMentionsPlugin, { NewMentionsPluginHandles } from "../drops/create/lexical/plugins/mentions/MentionsPlugin";
import NewHashtagsPlugin, { NewHastagsPluginHandles } from "../drops/create/lexical/plugins/hashtags/HashtagsPlugin";
import { MaxLengthPlugin } from "../drops/create/lexical/plugins/MaxLengthPlugin";
import DragDropPastePlugin from "../drops/create/lexical/plugins/DragDropPastePlugin";
import EnterKeyPlugin from "../drops/create/lexical/plugins/enter/EnterKeyPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import CreateDropEmojiPicker from "./CreateDropEmojiPicker";
import useCapacitor from "@/hooks/useCapacitor";
import EmojiPlugin from "../drops/create/lexical/plugins/emoji/EmojiPlugin";
import { EmojiNode } from "../drops/create/lexical/nodes/EmojiNode";
import { SAFE_MARKDOWN_TRANSFORMERS } from "@/components/drops/create/lexical/transformers/markdownTransformers";
import PlainTextPastePlugin from "@/components/drops/create/lexical/plugins/PlainTextPastePlugin";
export interface CreateDropInputHandles {
  clearEditorState: () => void;
  focus: () => void;
}

// Create a custom command
const DISABLE_EDIT_COMMAND = createCommand("DISABLE_EDIT");

// Create a custom plugin to handle disabling
function DisableEditPlugin(t0) {
  const $ = _c(4);
  const {
    disabled
  } = t0;
  const [editor] = useLexicalComposerContext();
  let t1;
  let t2;
  if ($[0] !== disabled || $[1] !== editor) {
    t1 = () => {
      if (disabled) {
        return editor.registerCommand(DISABLE_EDIT_COMMAND, _temp, COMMAND_PRIORITY_CRITICAL);
      }
      return _temp2;
    };
    t2 = [editor, disabled];
    $[0] = disabled;
    $[1] = editor;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return null;
}
function _temp2() {}
function _temp() {
  return true;
}
const CreateDropInput = forwardRef<CreateDropInputHandles, {
  readonly waveId: string;
  readonly editorState: EditorState | null;
  readonly type: ActiveDropAction | null;
  readonly canSubmit: boolean;
  readonly isStormMode: boolean;
  readonly submitting: boolean;
  readonly isDropMode: boolean;
  readonly onDrop?: () => void;
  readonly onEditorState: (editorState: EditorState) => void;
  readonly onReferencedNft: (referencedNft: ReferencedNft) => void;
  readonly onMentionedUser: (mentionedUser: Omit<MentionedUser, "current_handle">) => void;
}>((t0, ref) => {
  const $ = _c(70);
  const {
    waveId,
    editorState,
    type,
    canSubmit,
    isStormMode,
    isDropMode,
    submitting,
    onEditorState,
    onReferencedNft,
    onMentionedUser,
    onDrop
  } = t0;
  const {
    isCapacitor
  } = useCapacitor();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [MentionNode, HashtagNode, RootNode, HeadingNode, ListNode, ListItemNode, QuoteNode, CodeNode, CodeHighlightNode, TableNode, TableCellNode, TableRowNode, AutoLinkNode, LinkNode, HorizontalRuleNode, ImageNode, EmojiNode];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = !submitting;
  let t3;
  if ($[1] !== editorState || $[2] !== t2) {
    t3 = {
      namespace: "User Drop",
      nodes: t1,
      editorState,
      editable: t2,
      onError(error) {
        throw error;
      },
      theme: ExampleTheme
    };
    $[1] = editorState;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const editorConfig = t3;
  let t4;
  if ($[4] !== onEditorState) {
    t4 = editorState_0 => onEditorState(editorState_0);
    $[4] = onEditorState;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const onEditorStateChange = t4;
  let t5;
  if ($[6] !== onMentionedUser) {
    t5 = user => {
      onMentionedUser(user);
    };
    $[6] = onMentionedUser;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const onMentionedUserAdded = t5;
  let t6;
  if ($[8] !== onReferencedNft) {
    t6 = hashtag => onReferencedNft(hashtag);
    $[8] = onReferencedNft;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const onHashtagAdded = t6;
  let t7;
  if ($[10] !== isDropMode || $[11] !== isStormMode || $[12] !== type) {
    t7 = () => {
      if (isStormMode) {
        return "Add to the storm";
      }
      if (!type) {
        return isDropMode ? "Create a drop" : "Create a post";
      }
      switch (type) {
        case ActiveDropAction.REPLY:
          {
            return isDropMode ? "Drop a reply" : "Post a reply";
          }
        case ActiveDropAction.QUOTE:
          {
            return isDropMode ? "Quote a drop" : "Post a quote";
          }
        default:
          {
            assertUnreachable(type);
            return "";
          }
      }
    };
    $[10] = isDropMode;
    $[11] = isStormMode;
    $[12] = type;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const getPlaceHolderText = t7;
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    const urlRegExp = new RegExp(/((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+|(?:www.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)((?:\/[+~%/.\w-_]*)?\??(?:[-+=&;%@.\w_]*)#?(?:[\w]*))?)/);
    t8 = function validateUrl(url) {
      return url === "https://" || urlRegExp.test(url);
    };
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const validateUrl = t8;
  const clearEditorRef = useRef(null);
  const editorRef = useRef(null);
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      clearEditorRef.current?.clearEditorState();
    };
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const clearEditorState = t9;
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => ({
      clearEditorState,
      focus: () => {
        (editorRef.current?.querySelector("[contenteditable=\"true\"]") as HTMLElement)?.focus();
      }
    });
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  useImperativeHandle(ref, t10);
  const mentionsPluginRef = useRef(null);
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => !!mentionsPluginRef.current?.isMentionsOpen();
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const isMentionsOpen = t11;
  const hashtagPluginRef = useRef(null);
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    const isHashtagsOpen = () => !!hashtagPluginRef.current?.isHashtagsOpen();
    t12 = () => !isMentionsOpen() && !isHashtagsOpen();
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const canSubmitWithEnter = t12;
  const canSubmitRef = useRef(canSubmit);
  const onDropRef = useRef(onDrop);
  let t13;
  let t14;
  if ($[19] !== canSubmit) {
    t13 = () => {
      canSubmitRef.current = canSubmit;
    };
    t14 = [canSubmit];
    $[19] = canSubmit;
    $[20] = t13;
    $[21] = t14;
  } else {
    t13 = $[20];
    t14 = $[21];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[22] !== onDrop) {
    t15 = () => {
      onDropRef.current = onDrop;
    };
    t16 = [onDrop];
    $[22] = onDrop;
    $[23] = t15;
    $[24] = t16;
  } else {
    t15 = $[23];
    t16 = $[24];
  }
  useEffect(t15, t16);
  let t17;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => {
      if (!canSubmitRef.current || !onDropRef.current) {
        return;
      }
      onDropRef.current();
    };
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  const handleSubmit = t17;
  let t18;
  if ($[26] !== submitting) {
    t18 = e => {
      const target = e.currentTarget;
      if (target && !submitting) {
        Promise.resolve().then(() => {
          target.focus();
          const selection = window.getSelection();
          if (selection && selection.rangeCount === 0) {
            const range = document.createRange();
            range.selectNodeContents(target);
            range.collapse(false);
            selection.addRange(range);
          }
        });
      }
    };
    $[26] = submitting;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  const t19 = `tw-max-h-[40vh] editor-input-one-liner tw-resize-none tw-form-input tw-block tw-w-full tw-rounded-lg tw-border-0 tw-bg-iron-900 tw-text-white tw-font-normal tw-caret-primary-400 tw-shadow-sm tw-ring-1 tw-ring-inset tw-ring-iron-700 placeholder:tw-text-iron-500 focus:tw-outline-none focus:tw-bg-iron-950 focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400 tw-text-base sm:tw-text-sm tw-leading-6 tw-transition tw-duration-300 tw-ease-out 
        tw-pl-3 tw-py-2.5 tw-scrollbar-thin tw-scrollbar-thumb-iron-600 tw-scrollbar-track-iron-900 ${submitting ? "tw-opacity-50 tw-cursor-default" : ""} ${isCapacitor ? "tw-pr-[35px]" : "tw-pr-[40px]"}`;
  let t20;
  if ($[28] !== t18 || $[29] !== t19) {
    t20 = <ContentEditable spellCheck={true} autoCorrect="on" onClick={t18} className={t19} />;
    $[28] = t18;
    $[29] = t19;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  let t21;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <CreateDropEmojiPicker />;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  let t22;
  if ($[32] !== t20) {
    t22 = <div className="tw-relative">{t20}{t21}</div>;
    $[32] = t20;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  const t23 = `editor-placeholder ${submitting ? "tw-opacity-50" : ""}`;
  const t24 = getPlaceHolderText();
  let t25;
  if ($[34] !== t23 || $[35] !== t24) {
    t25 = <span className={t23}>{t24}</span>;
    $[34] = t23;
    $[35] = t24;
    $[36] = t25;
  } else {
    t25 = $[36];
  }
  let t26;
  if ($[37] !== t22 || $[38] !== t25) {
    t26 = <RichTextPlugin contentEditable={t22} placeholder={t25} ErrorBoundary={LexicalErrorBoundary} />;
    $[37] = t22;
    $[38] = t25;
    $[39] = t26;
  } else {
    t26 = $[39];
  }
  let t27;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <HistoryPlugin />;
    $[40] = t27;
  } else {
    t27 = $[40];
  }
  let t28;
  if ($[41] !== onEditorStateChange) {
    t28 = <OnChangePlugin onChange={onEditorStateChange} />;
    $[41] = onEditorStateChange;
    $[42] = t28;
  } else {
    t28 = $[42];
  }
  let t29;
  if ($[43] !== onMentionedUserAdded || $[44] !== waveId) {
    t29 = <NewMentionsPlugin waveId={waveId} onSelect={onMentionedUserAdded} ref={mentionsPluginRef} />;
    $[43] = onMentionedUserAdded;
    $[44] = waveId;
    $[45] = t29;
  } else {
    t29 = $[45];
  }
  let t30;
  if ($[46] !== onHashtagAdded) {
    t30 = <NewHashtagsPlugin onSelect={onHashtagAdded} ref={hashtagPluginRef} />;
    $[46] = onHashtagAdded;
    $[47] = t30;
  } else {
    t30 = $[47];
  }
  let t31;
  let t32;
  let t33;
  let t34;
  let t35;
  let t36;
  let t37;
  let t38;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <MaxLengthPlugin maxLength={25000} />;
    t32 = <DragDropPastePlugin />;
    t33 = <ListPlugin />;
    t34 = <PlainTextPastePlugin />;
    t35 = <MarkdownShortcutPlugin transformers={SAFE_MARKDOWN_TRANSFORMERS} />;
    t36 = <TabIndentationPlugin />;
    t37 = <LinkPlugin validateUrl={validateUrl} />;
    t38 = <ClearEditorPlugin ref={clearEditorRef} />;
    $[48] = t31;
    $[49] = t32;
    $[50] = t33;
    $[51] = t34;
    $[52] = t35;
    $[53] = t36;
    $[54] = t37;
    $[55] = t38;
  } else {
    t31 = $[48];
    t32 = $[49];
    t33 = $[50];
    t34 = $[51];
    t35 = $[52];
    t36 = $[53];
    t37 = $[54];
    t38 = $[55];
  }
  let t39;
  let t40;
  if ($[56] !== submitting) {
    t39 = <DisableEditPlugin disabled={submitting} />;
    t40 = <EnterKeyPlugin handleSubmit={handleSubmit} canSubmitWithEnter={canSubmitWithEnter} disabled={submitting} />;
    $[56] = submitting;
    $[57] = t39;
    $[58] = t40;
  } else {
    t39 = $[57];
    t40 = $[58];
  }
  let t41;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <EmojiPlugin />;
    $[59] = t41;
  } else {
    t41 = $[59];
  }
  let t42;
  if ($[60] !== t26 || $[61] !== t28 || $[62] !== t29 || $[63] !== t30 || $[64] !== t39 || $[65] !== t40) {
    t42 = <div className="tw-flex tw-items-end tw-gap-x-3"><div className="tw-relative tw-w-full">{t26}{t27}{t28}{t29}{t30}{t31}{t32}{t33}{t34}{t35}{t36}{t37}{t38}{t39}{t40}{t41}</div></div>;
    $[60] = t26;
    $[61] = t28;
    $[62] = t29;
    $[63] = t30;
    $[64] = t39;
    $[65] = t40;
    $[66] = t42;
  } else {
    t42 = $[66];
  }
  let t43;
  if ($[67] !== editorConfig || $[68] !== t42) {
    t43 = <div className="tailwind-scope" ref={editorRef}><LexicalComposer initialConfig={editorConfig}>{t42}</LexicalComposer></div>;
    $[67] = editorConfig;
    $[68] = t42;
    $[69] = t43;
  } else {
    t43 = $[69];
  }
  return t43;
});
CreateDropInput.displayName = "CreateDropInput";
export default CreateDropInput;